import { Autocomplete, Box, Checkbox, Stack, SxProps, TextField, Theme, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { HTMLAttributes, Key, ReactNode, SyntheticEvent, useContext, useMemo } from 'react';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { IContactSupplierDetails } from '../../../shared/services/contact-supplier/contact-supplier.model';
import { CheckboxVirtualizedListbox, VirtualizedListbox, createVirtualizedListbox } from './virtualized-listbox';

// A name over a caption line (20px each) plus the option padding, with or without a checkbox.
const TWO_LINE_OPTION_HEIGHT = 52;
const TwoLineVirtualizedListbox = createVirtualizedListbox(TWO_LINE_OPTION_HEIGHT);

interface SupplierPickerBaseProps {
  suppliers: IContactSupplierDetails[];
  id?: string;
  /** Defaults to the organisation's term for a supplier. */
  label?: string;
  placeholder?: string;
  error?: boolean;
  /** Rendered as the TextField's own helper text. */
  helperText?: ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** Applied to the Autocomplete root. */
  sx?: SxProps<Theme>;
  /** Size of the chips a multiple picker shows for its selection. Defaults to `size`. */
  chipSize?: 'small' | 'medium';
  /** A caption line under the supplier's name in the dropdown. */
  renderOptionSecondary?: (supplier: IContactSupplierDetails) => ReactNode;
  getOptionDisabled?: (supplier: IContactSupplierDetails) => boolean;
}

interface SingleSupplierPickerProps extends SupplierPickerBaseProps {
  multiple?: false;
  /** The selected supplier's id. */
  value: string | null | undefined;
  /**
   * `id` and `supplier` are null when the field is cleared, and when a freeSolo picker commits
   * text that is not a supplier, in which case `freeText` carries that text.
   */
  onChange: (id: string | null, supplier: IContactSupplierDetails | null, freeText?: string) => void;
  /** Allow a name that is not one of the suppliers. */
  freeSolo?: boolean;
  /** freeSolo only: the text the field holds when `value` matches no supplier. */
  inputValue?: string;
  onInputChange?: (text: string) => void;
}

interface MultipleSupplierPickerProps extends SupplierPickerBaseProps {
  multiple: true;
  /** The selected suppliers' ids. */
  value: string[] | null | undefined;
  onChange: (ids: string[], suppliers: IContactSupplierDetails[]) => void;
}

export type SupplierPickerProps = SingleSupplierPickerProps | MultipleSupplierPickerProps;

type PickerValue = IContactSupplierDetails | string;

/**
 * A searchable supplier dropdown for long supplier lists: sorted once per list, and virtualised so
 * only the rows in view are mounted. Takes and returns supplier ids. Looks like the plain supplier
 * Autocompletes it replaces (outlined TextField, label, helper text).
 */
export const SupplierPicker = (props: SupplierPickerProps) => {
  const {
    suppliers,
    id,
    label,
    placeholder,
    error,
    helperText,
    disabled,
    size,
    sx,
    chipSize,
    renderOptionSecondary,
    getOptionDisabled,
  } = props;
  const multiple = props.multiple === true;
  const freeSolo = !props.multiple && props.freeSolo === true;
  const screenLabelsContext = useContext(ScreenLabelsContext);

  const sortedSuppliers = useMemo(
    () => suppliers.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [suppliers]
  );
  const suppliersById = useMemo(
    () => new Map(sortedSuppliers.map((supplier) => [supplier.id, supplier])),
    [sortedSuppliers]
  );

  // A multiple value keeps its identity while the ids are unchanged, because MUI clears the typed
  // search text whenever the value changes.
  const selectedIds = props.multiple ? props.value : undefined;
  const selectedSuppliers = useMemo(
    () => (selectedIds || [])
      .map((supplierId) => suppliersById.get(supplierId))
      .filter((supplier): supplier is IContactSupplierDetails => Boolean(supplier)),
    [selectedIds, suppliersById]
  );

  let value: PickerValue | PickerValue[] | null;
  if (props.multiple) {
    value = selectedSuppliers;
  } else {
    const selectedSupplier = props.value ? suppliersById.get(props.value) ?? null : null;
    value = selectedSupplier ?? (freeSolo && props.inputValue ? props.inputValue : null);
  }

  const handleChange = (_event: SyntheticEvent, newValue: PickerValue | PickerValue[] | null) => {
    if (props.multiple) {
      const picked = (Array.isArray(newValue) ? newValue : [])
        .filter((supplier): supplier is IContactSupplierDetails => typeof supplier !== 'string');
      props.onChange(picked.map((supplier) => supplier.id), picked);
      return;
    }
    if (typeof newValue === 'string') {
      props.onChange(null, null, newValue);
      return;
    }
    const picked = Array.isArray(newValue) ? null : newValue;
    props.onChange(picked ? picked.id : null, picked);
  };

  const onInputChange = !props.multiple && props.onInputChange;
  const handleInputChange = onInputChange
    ? (_event: SyntheticEvent, text: string) => onInputChange(text)
    : undefined;

  const listboxComponent = renderOptionSecondary
    ? TwoLineVirtualizedListbox
    : multiple ? CheckboxVirtualizedListbox : VirtualizedListbox;

  return (
    <Autocomplete<PickerValue, boolean, false, boolean>
      id={id}
      options={sortedSuppliers}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      multiple={multiple}
      freeSolo={freeSolo}
      disableCloseOnSelect={multiple}
      autoHighlight
      disabled={disabled}
      size={size}
      sx={sx}
      ChipProps={chipSize ? { size: chipSize } : undefined}
      ListboxComponent={listboxComponent}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
      getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
      isOptionEqualToValue={(option, selected) =>
        typeof option !== 'string' && typeof selected !== 'string' && !!selected && option.id === selected.id}
      getOptionDisabled={getOptionDisabled
        ? (option) => typeof option !== 'string' && getOptionDisabled(option)
        : undefined}
      renderOption={(optionProps, option, { selected }) => {
        const { key, ...restProps } = optionProps as { key: Key } & HTMLAttributes<HTMLLIElement>;
        const supplier = option as IContactSupplierDetails;
        return (
          <Box component='li' key={key} {...restProps}>
            {multiple && (
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {renderOptionSecondary ? (
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant='body2' noWrap>{supplier.name}</Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                  {renderOptionSecondary(supplier)}
                </Typography>
              </Stack>
            ) : (
              <Box component='span' sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {supplier.name}
              </Box>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
